import React, { useEffect, useState } from "react";
import "./Main.css";
import { useSelector } from "react-redux";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Card, CardContent, CardActions, Chip, Box, LinearProgress, Alert, AlertTitle, useTheme } from "@mui/material";
import { Mail as MailIcon, Message as MessageIcon, CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { isProfileCompleted } from "../../../../utils";
import ProfileStepInformationStatuses from "../../../Profile/Components/ProfileStepInformationStatuses";
import { getStatus } from "./utils";
import { API_URL, createAxiosInstance } from "../../../../api";
import { useProfileStatus } from "../../../../hooks/useProfileStatus";

const Main = (props) => {
  const theme = useTheme();
  const profileUser = useSelector((state) => state.profile.user);
  const { _id, status } = useSelector((state) => state.profile.user);
  const { userId } = useSelector((state) => state.main);
  const { profileDisabled, adminDisabled } = useSelector((state) => state.main);
  const { messages } = useSelector((state) => state.profile);

  const navigate = useNavigate();
  const {
    createdAt,
    billedAt,
    status: accountStatus,
  } = useSelector((state) => state.main);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const [hasPaid, setHasPaid] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [hasSubjects, setHasSubjects] = useState(false);
  const [subjectsLoaded, setSubjectsLoaded] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [isPaymentExempt, setIsPaymentExempt] = useState(false);

  // Use the new status service with proper completion check
  const statusInfo = useProfileStatus(isProfileCompleted, hasSubjects, hasPaid, cancelAtPeriodEnd);
  useEffect(() => {
    const count = messages.filter((message) => !message.isRead).length;
    setUnreadCount(count);
  }, [messages]);

  // Check payment status
  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!userId) return;
      
      try {
        const response = await createAxiosInstance().post(
          `${API_URL}/api/billings/hasPaid/${userId}`
        );
        // Handle string values "Paid" and "Not Paid" from the API
        const paidStatus = response.data?.paid;
        const isPaid = paidStatus === "Paid" || paidStatus === true;
        setHasPaid(isPaid);

        if (response.data?.cancelAtPeriodEnd) {
          setCancelAtPeriodEnd(true);
        }

        if (response.data?.paymentExempt) {
          setIsPaymentExempt(true);
        }
        
        // Check if user is on trial
        if (response.data?.onTrial) {
          setTrialStatus({
            onTrial: true,
            trialEndDate: response.data.trialEndDate,
          });
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        setHasPaid(false);
      }
    };

    checkPaymentStatus();
  }, [userId]);

  // Fetch detailed trial status
  useEffect(() => {
    const fetchTrialStatus = async () => {
      if (!userId) return;
      
      try {
        const response = await createAxiosInstance().get(
          `${API_URL}/api/billings/trial/status/${userId}`
        );
        
        if (response.data?.success && response.data?.hasTrial) {
          console.log("✅ User has active trial:", {
            trialEndDate: response.data.trialEndDate,
            daysRemaining: response.data.daysRemaining,
            isExpired: response.data.isExpired,
            isExpiringSoon: response.data.isExpiringSoon,
          });
          setTrialStatus({
            onTrial: true,
            trialEndDate: response.data.trialEndDate,
            daysRemaining: response.data.daysRemaining,
            isExpiringSoon: response.data.isExpiringSoon,
            isExpired: response.data.isExpired,
          });
        } else {
          console.log("ℹ️  User does not have active trial:", response.data);
          setTrialStatus(null);
        }
      } catch (error) {
        console.error("❌ Error fetching trial status:", error);
        setTrialStatus(null);
      }
    };

    fetchTrialStatus();
  }, [userId]);

  // Check subjects status
  useEffect(() => {
    const checkSubjectsStatus = async () => {
      if (!userId) return;
      
      try {
        const response = await createAxiosInstance().get(
          `${API_URL}/api/subjects/user/${userId}`
        );
        const subjects = response.data || [];
        setHasSubjects(subjects.length > 0);
        setSubjectsLoaded(true);
      } catch (error) {
        console.error("Error checking subjects status:", error);
        setHasSubjects(false);
        setSubjectsLoaded(true);
      }
    };

    checkSubjectsStatus();
  }, [userId]);
  // const subscriptionStrings = {
  //   TrialEnded:
  //     "Your trial period is ended. Please subscribe to make your account active",
  //   Trial: "Your account is in trial period until ",
  //   Expired:
  //     "Your account is expired. Please subscribe to make your account active",
  //   Paid: "Your account is active until ",
  // };

  // let subscription = "",
  //   date = "";
  //
  // if (billedAt) {
  //   // Convert the billedAt value into a Date object (assuming billedAt is in a valid date format)
  //   const billedAtDate = new Date(billedAt);
  //   const oneYearAgo = new Date();
  //   oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  //
  //   // Check if billedAt is before one year ago
  //   const isExpired = billedAtDate < oneYearAgo;
  //   // subscription = subscriptionStrings[isExpired ? "Expired" : "Paid"];
  //
  //   if (!isExpired) {
  //     // Calculate when the data will expire, which is one year after billedAt
  //     const expirationDate = new Date(billedAtDate);
  //     expirationDate.setFullYear(expirationDate.getFullYear() + 1);
  //     const options = { day: "numeric", month: "long", year: "numeric" };
  //     date = expirationDate.toLocaleDateString("en-GB", options); // Format the date as YYYY-MM-DD
  //   }
  // } else {
  //   // Convert the createdAt value into a Date object (assuming createdAt is in a valid date format)
  //   const createdAtDate = new Date(createdAt);
  //   const threeMonthsAgo = new Date();
  //   threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  //
  //   // Check if createdAt is before three months ago
  //   const isExpired = createdAtDate < threeMonthsAgo;
  //   subscription = subscriptionStrings[isExpired ? "TrialEnded" : "Trial"];
  //
  //   if (!isExpired) {
  //     // Calculate when the trial will expire, which is three months after createdAt
  //     const expirationDate = new Date(createdAtDate);
  //     expirationDate.setMonth(expirationDate.getMonth() + 3);
  //     const options = { day: "numeric", month: "long", year: "numeric" };
  //     date = expirationDate.toLocaleDateString("en-GB", options);
  //   }
  // }
  const statusColor = (status, accountStatus) => {
    if (!_id) return "#da1e28"; // Not Created - Red
    if (adminDisabled) return "#da1e28"; // Admin disabled - Red
    if (profileDisabled) return "#ba4e00"; // Self-hidden - Orange
    
    if (status === 0 && accountStatus === 0) {
      if (isProfileCompleted(profileUser, hasSubjects) && hasPaid && !profileUser?.isSubmitted) {
        return "#00AB55"; // Completed with payment but not submitted - Green
      } else if (profileUser?.isSubmitted) {
        return "#ba4e00"; // Submitted but pending - Orange
      } else {
        return "#712d93"; // Draft/Incomplete - Purple
      }
    }
    
    if (status === 1 && accountStatus === 1) {
      return "#24A148"; // Live - Green
    }
    
    return "#da1e28"; // Default - Red
  };
  const statusMessage = getStatus(
    profileUser,
    status,
    accountStatus,
    _id,
    hasSubjects,
    profileDisabled,
    adminDisabled
  );

  // Calculate progress for the profile steps (always 6 total steps)
  const getProgressValue = () => {
    if (!profileUser) return 0;
    
    let completedSteps = 0;
    const totalSteps = 6; // Always 6 total steps (including optional)
    
    // Check required steps
    if (hasSubjects) completedSteps++;
    if (profileUser.online || profileUser.faceToFace) completedSteps++;
    if (profileUser.avatar) completedSteps++;
    if (Object.keys(profileUser.qualifications || {}).some(k => profileUser.qualifications[k] !== -1)) completedSteps++;
    if (hasPaid) completedSteps++;
    
    // Check if optional step is completed (contact information)
    const isOptionalStepCompleted = (profileUser.email && profileUser.email.trim() !== "") || 
      (profileUser.phone && profileUser.phone.trim() !== "");
    
    // Count optional step if completed
    if (isOptionalStepCompleted) {
      completedSteps++;
    }
    
    return Math.round((completedSteps / totalSteps) * 100);
  };

  const progressValue = getProgressValue();
  
  // Calculate completed and total steps for display
  const isOptionalStepCompleted = (profileUser?.email && profileUser.email.trim() !== "") || 
    (profileUser?.phone && profileUser.phone.trim() !== "");
  
  const completedSteps = Math.round((progressValue / 100) * 6);
  const totalSteps = 6;

  return (
    
    <Box sx={{ px: { xs: 2, sm: 2, md: 3 } }}>
    <Typography variant="h4" component="h1" sx={{ 
      mb: { xs: 2, sm: 3 }, 
      fontWeight: 600,
      fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' }
    }}>
      Dashboard
    </Typography>
    
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 3, 
    
      maxWidth: '100%'
    }}>
      {/* Top Row - Two Cards with Different Sizes */}
      <Box sx={{ 
        display: 'flex', 
        gap: { xs: 2, sm: 2 }, 
        flexDirection: { xs: 'column', md: 'row' },
        mb: { xs: 2, sm: 2 }
      }}>
        {/* Unread Messages Card - Medium Size */}
        <Card 
          elevation={2} 
          sx={{ 
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': { elevation: 4 },
            flex: { xs: 1, md: '0 0 280px' },
            minHeight: { xs: 110, sm: 130 }
          }}
        >
          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <MailIcon sx={{ color: theme.palette.primary.main, fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="h6" component="h3" sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
              }}>
                Unread Messages
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
                  color: theme.palette.primary.main
                }}
              >
                {unreadCount}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Combined Profile Overview Card - Larger */}
        <Card 
          elevation={2} 
          sx={{ 
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': { elevation: 4 },
            flex: 1,
            minHeight: { xs: 110, sm: 130 }
          }}
        >
          <CardContent sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'space-between',
            p: { xs: 2, sm: 2.5 }
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: { xs: 'flex-start', sm: 'center' }, 
              justifyContent: 'space-between', 
              mb: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: { xs: 20, sm: 24 } }} />
                <Typography variant="h6" component="h3" sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                }}>
                  Profile Overview
                </Typography>
              </Box>
              <Chip 
                label={statusInfo.utils?.getMessage ? statusInfo.utils.getMessage() : statusInfo.status.replace(/_/g, ' ')} 
                color={statusInfo.status === 'LIVE' ? 'success' : statusInfo.status === 'CANCELLING' ? 'warning' : statusInfo.status === 'DRAFT' ? 'warning' : 'default'}
                variant="outlined"
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                  height: { xs: 24, sm: 28 },
                  backgroundColor: statusInfo.color === '#24A148' ? 'rgba(36, 161, 72, 0.1)' :
                                 statusInfo.color === '#f59e0b' ? 'rgba(245, 158, 11, 0.1)' :
                                 statusInfo.color === '#da1e28' ? 'rgba(218, 30, 40, 0.1)' :
                                 `${theme.palette.primary.main}1a`,
                  borderColor: statusInfo.color,
                  color: statusInfo.color,
                  '& .MuiChip-label': {
                    px: { xs: 1, sm: 1.5 }
                  }
                }}
              />
            </Box>

            <Box>
              {/* Progress Section */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
                  Profile Steps Progress
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                }}>
                  {completedSteps}/{totalSteps}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progressValue} 
                sx={{ 
                  height: { xs: 6, sm: 8 }, 
                  borderRadius: 4,
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: progressValue === 100 ? theme.palette.success.main : theme.palette.primary.main,
                    borderRadius: 4
                  }
                }} 
              />
              </Box>
            </CardContent>
          </Card>
        </Box>
      

      {/* Profile Steps Section */}
      <Card 
        elevation={2} 
        sx={{ 
          borderRadius: 2,
          transition: 'all 0.2s ease-in-out',
          '&:hover': { elevation: 4 }
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          {_id && status === 0 && !profileUser?.isSubmitted && isProfileCompleted(profileUser, hasSubjects) && hasPaid && (
            <Typography
              variant="body2"
              sx={{
                color: "#d32f2f",
                fontWeight: 600,
                fontSize: "0.85rem",
                mb: 1,
                textAlign: "right",
              }}
            >
              You've updated your profile — please resubmit it for review.
            </Typography>
          )}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 0 }
          }}>
            <Typography variant="h5" component="h3" sx={{
              fontWeight: 600,
              fontSize: { xs: '1.15rem', sm: '1.25rem', md: '1.5rem' }
            }}>
              Profile Steps
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              width: { xs: '100%', sm: 'auto' }
            }}>
            {status === 1 && accountStatus === 1 && !profileDisabled && !adminDisabled && (
              <Button
                variant="outlined"
                color="secondary"
                sx={{ 
                  textWrap: "nowrap", 
                  minWidth: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '0.875rem', sm: '0.9rem' },
                  py: { xs: 0.75, sm: 1 }
                }}
                onClick={() => window.open(`/tutor/${_id}`)}
              >
                <strong>View profile</strong>
              </Button>
            )}
              <Button
                variant="contained"
                onClick={() =>
                    isProfileCompleted(profileUser, hasSubjects) && hasPaid && status === 0 ? (!profileUser.isSubmitted ? navigate("/profile?step=submit")
                      : navigate("/profile?step=subjects"))
                    : navigate("/profile")
                }
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  minWidth: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '0.875rem', sm: '0.9rem' },
                  py: { xs: 0.75, sm: 1 },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                {!_id
                  ? "Create profile"
                  : _id &&
                    status === 0 &&
                    !profileUser?.isSubmitted && isProfileCompleted(profileUser, hasSubjects) && hasPaid
                  ? "Submit profile"
                  : "Edit profile"}
              </Button>
            </Box>
          </Box>

          {/* Free Trial Banner */}
          {trialStatus?.onTrial && !trialStatus?.isExpired && !isPaymentExempt && (
            <Box sx={{ 
              p: { xs: 2, sm: 2.5, md: 3 }, 
              backgroundColor: trialStatus?.isExpiringSoon ? '#fff3cd' : '#f0f9ff', 
              border: `1px solid ${trialStatus?.isExpiringSoon ? '#ffeaa7' : '#bfdbfe'}`,
              borderRadius: 2,
              mb: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ 
                  fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  lineHeight: 1,
                  flexShrink: 0,
                  mt: 0.25
                }}>
                  {trialStatus?.isExpiringSoon ? '⏰' : '🎉'}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                    color: trialStatus?.isExpiringSoon ? '#856404' : '#1e40af',
                    mb: 1,
                    lineHeight: 1.3
                  }}>
                    {trialStatus?.isExpiringSoon 
                      ? `Free Trial Ending Soon – ${trialStatus.daysRemaining} ${trialStatus.daysRemaining === 1 ? 'Day' : 'Days'} Remaining!`
                      : 'Enjoy Your First 3 Months Free!'}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    fontWeight: 400,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    color: trialStatus?.isExpiringSoon ? '#856404' : '#1e3a8a',
                    lineHeight: 1.6,
                    mb: 1.5
                  }}>
                    {trialStatus?.isExpiringSoon 
                      ? `Your free trial ends on ${new Date(trialStatus.trialEndDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}. Your tutoring profile will stay visible to potential clients, giving you a great opportunity to grow.`
                      : 'As an existing user, you now have 3 months of full access at no cost. Your tutoring profile will stay visible to potential clients, giving you a great opportunity to grow.'}
                  </Typography>

                  <Typography variant="body2" sx={{ 
                    fontWeight: 400,
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                    color: trialStatus?.isExpiringSoon ? '#856404' : '#1e3a8a',
                    lineHeight: 1.6,
                    mb: trialStatus?.isExpiringSoon ? 2 : 0
                  }}>
                    {trialStatus?.isExpiringSoon 
                      ? 'Subscribe now to keep your profile active and continue reaching students.'
                      : "We'll send you a quick reminder before your free period ends – and we'd love for you to stay with us!"}
                  </Typography>

                  {trialStatus?.isExpiringSoon && (
                    <Button
                      variant="contained"
                      size="medium"
                      onClick={() => navigate("/profile?step=subscription")}
                      sx={{ 
                        textTransform: 'none',
                        fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                        fontWeight: 600,
                        py: { xs: 1, sm: 1.25 },
                        px: { xs: 2, sm: 3 },
                        backgroundColor: '#f39c12',
                        borderRadius: 1.5,
                        '&:hover': {
                          backgroundColor: '#e67e22'
                        }
                      }}
                    >
                      Subscribe Now
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Status Banner */}
          {statusInfo.status === 'DRAFT' && (
            <Box sx={{ 
              p: { xs: 1.25, sm: 1.5 }, 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7',
              borderRadius: 1,
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 0.75, sm: 1 }
            }}>
              <Box sx={{ 
                width: { xs: 18, sm: 20 }, 
                height: { xs: 18, sm: 20 }, 
                minWidth: { xs: 18, sm: 20 },
                borderRadius: '50%', 
                backgroundColor: '#f39c12',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: { xs: '11px', sm: '12px' },
                fontWeight: 'bold',
                flexShrink: 0,
                mt: { xs: 0.25, sm: 0 }
              }}>
                !
              </Box>
              <Typography variant="body2" color="#856404" sx={{ 
                fontWeight: 500,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                lineHeight: 1.5
              }}>
                {
                  isProfileCompleted(profileUser, hasSubjects) ? 'Your profile is not live. Submit the profile to make it visible to students.' : 'Your profile is not live. Complete the steps below to make it visible to students.'
                }
              </Typography>
            </Box>
          )}
        </CardContent>

        <CardActions sx={{ pt: 0, px: { xs: 2, sm: 2.5 }, pb: 2, mb: 2 }}>
          <Box sx={{ maxWidth: "100%", width: "100%" }}>
            <ProfileStepInformationStatuses />
          </Box>
        </CardActions>

        {/* Payment Required Card */}
        {!hasPaid && subjectsLoaded && isProfileCompleted(profileUser, hasSubjects) && (
          <Card 
            elevation={2}
            sx={{
              borderRadius: 2,
              border: '1px solid #e3f2fd',
              backgroundColor: '#f8f9ff',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                elevation: 4,
                transform: 'translateY(-1px)'
              }
            }}
          >
            <CardContent sx={{ p: { xs: 1.75, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2.5 } }}>
                <Box sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  minWidth: { xs: 36, sm: 40 },
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: { xs: '16px', sm: '18px' },
                  boxShadow: '0 2px 8px rgba(113, 45, 148, 0.2)',
                  flexShrink: 0
                }}>
                  💡
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    color: theme.palette.primary.main, 
                    mb: { xs: 0.75, sm: 1 },
                    fontSize: { xs: '1rem', sm: '1.1rem' }
                  }}>
                    Plan Required
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 500, 
                    color: '#2c3e50', 
                    mb: { xs: 0.75, sm: 1 },
                    lineHeight: 1.5,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                }}>
                  To publish your profile and start receiving students, please choose a payment plan.
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: '#6c757d',
                    lineHeight: 1.4,
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                }}>
                  Complete your subscription to make your profile visible to students.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
      </Card>
    </Box>
    </Box>
  );
};

export default Main;
